import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Reply } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const ReplyDialog = ({ ticket, onClose, onSent }) => {
  const [channel, setChannel] = useState('email');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => { if (ticket) { setChannel(ticket.contact_method === 'phone' ? 'sms' : 'email'); setBody(''); } }, [ticket]);
  const send = async () => {
    if (!body.trim()) return toast.error('Write a reply first');
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/helpconnect-support-requests/${ticket.id}/reply`, { body, channel });
      toast.success(channel === 'sms' ? 'Text sent' : 'Email sent');
      onSent(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the reply');
      if (e.response?.status === 502) onSent(null);
    } finally { setSending(false); }
  };
  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ticket-reply-dialog">
        <DialogHeader>
          <DialogTitle>Reply to {ticket?.number}</DialogTitle>
          <DialogDescription>{ticket?.participant_name} asked to be contacted by {ticket?.contact_method}: {ticket?.contact_value}. The reply also shows in their HELPConnect Support page.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant={channel === 'email' ? 'default' : 'outline'} onClick={() => setChannel('email')} data-testid="ticket-reply-channel-email"><Mail className="h-4 w-4 mr-1" />Email</Button>
          <Button size="sm" variant={channel === 'sms' ? 'default' : 'outline'} onClick={() => setChannel('sms')} data-testid="ticket-reply-channel-sms"><MessageSquare className="h-4 w-4 mr-1" />Text</Button>
        </div>
        <Textarea rows={5} maxLength={1500} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your reply" data-testid="ticket-reply-body" />
        {channel === 'sms' && <p className="text-xs text-muted-foreground">{body.length}/1500 characters. Long texts may arrive in several parts.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="ticket-reply-send">{sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send Reply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TicketRow = ({ r, onToggle, onReply }) => (
  <div className="flex flex-col md:flex-row md:items-start gap-3 p-4" data-testid={`support-request-row-${r.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{r.number} · {r.topic}</span>
        <Badge variant={r.status === 'open' ? 'default' : 'secondary'} data-testid={`support-request-status-${r.id}`}>{r.status === 'open' ? 'Open' : 'Resolved / Closed'}</Badge>
        {r.email_status === 'failed' && <Badge variant="destructive">Help desk email failed</Badge>}
        {r.status === 'open' && r.replies.at(-1)?.direction === 'inbound' && <Badge className="bg-amber-500 hover:bg-amber-500" data-testid={`support-request-awaiting-${r.id}`}>New text reply</Badge>}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        {r.participant_name} ({r.participant_id}) · {r.contact_method === 'email' ? 'Email' : 'Phone'}: {r.contact_value} · {fmt(r.created_at)}
        {r.resolved_at && ` · Closed ${fmt(r.resolved_at)}${r.resolved_by_name ? ` by ${r.resolved_by_name}` : ''}`}
      </div>
      <p className="text-sm mt-2 whitespace-pre-line">{r.message}</p>
      {r.replies.map((x) => (x.direction === 'inbound' ? (
        <div key={x.id} className="mt-2 ml-3 pl-3 border-l-2 border-amber-400 bg-amber-50/60 rounded-r py-1 text-sm" data-testid={`support-reply-${x.id}`}>
          <div className="text-xs text-amber-800 flex items-center gap-1"><MessageSquare className="h-3 w-3" />{r.participant_name} replied by text · {fmt(x.at)}</div>
          <p className="whitespace-pre-line">{x.body}</p>
        </div>
      ) : (
        <div key={x.id} className="mt-2 ml-3 pl-3 border-l-2 border-primary/40 text-sm" data-testid={`support-reply-${x.id}`}>
          <div className="text-xs text-muted-foreground">{x.staff_name} · {x.channel === 'sms' ? 'Text' : 'Email'} · {fmt(x.at)}{x.sent_status !== 'sent' && <span className="text-destructive"> · not delivered</span>}</div>
          <p className="whitespace-pre-line">{x.body}</p>
        </div>
      )))}
    </div>
    <div className="flex gap-2 shrink-0">
      <Button size="sm" variant="outline" onClick={() => onReply(r)} data-testid={`support-request-reply-${r.id}`}><Reply className="h-4 w-4 mr-1" />Reply</Button>
      <Button size="sm" variant={r.status === 'open' ? 'default' : 'outline'} onClick={() => onToggle(r)} data-testid={`support-request-toggle-${r.id}`}>{r.status === 'open' ? 'Resolve & Close' : 'Reopen'}</Button>
    </div>
  </div>
);

export const HelpDeskTickets = ({ showSettings = true, defaultFilter = 'open', onData }) => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [filter, setFilter] = useState(defaultFilter);
  const [q, setQ] = useState('');
  const [replying, setReplying] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/helpconnect-support-requests`).then((r) => { setData(r.data); setEmail(r.data.support_email); onData?.(r.data); })
      .catch(() => toast.error('Failed to load support requests'));
  }, [onData]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    try { await axios.post(`${API}/helpconnect-support-requests/${r.id}/${r.status === 'open' ? 'resolve' : 'reopen'}`); toast.success(r.status === 'open' ? 'Ticket resolved and closed' : 'Ticket reopened'); load(); }
    catch { toast.error('Could not update the ticket'); }
  };
  const saveEmail = async () => {
    try { await axios.put(`${API}/helpconnect-support-settings`, { support_email: email }); toast.success('Help desk email saved'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const term = q.trim().toLowerCase();
  const list = (data?.requests || []).filter((r) => (filter === 'all' || r.status === filter)
    && (!term || `${r.number} ${r.topic} ${r.message} ${r.participant_name} ${r.participant_id}`.toLowerCase().includes(term)));

  return (
    <Card data-testid="admin-support-requests-tab">
      <CardHeader className="space-y-3">
        <div><CardTitle className="text-base">Help Desk Tickets</CardTitle><CardDescription>Support requests participants send from HELPConnect. Reply by text or email, then resolve and close the ticket.</CardDescription></div>
        {showSettings && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm">Help desk email</span>
            <Input className="w-72" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="support-email-input" />
            <Button size="sm" variant="outline" onClick={saveEmail} data-testid="support-email-save">Save</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          {[['open', 'Open'], ['resolved', 'Resolved / Closed'], ['all', 'All']].map(([f, label]) => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} data-testid={`support-filter-${f}`}>{label}</Button>
          ))}
          <Input className="w-64" placeholder="Search tickets" value={q} onChange={(e) => setQ(e.target.value)} data-testid="support-ticket-search" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!data ? <Loader2 className="h-5 w-5 animate-spin m-6" /> : list.length === 0 ? <p className="p-6 text-sm text-muted-foreground" data-testid="support-requests-empty">No tickets match.</p> : (
          <div className="divide-y border-t">{list.map((r) => <TicketRow key={r.id} r={r} onToggle={toggle} onReply={setReplying} />)}</div>
        )}
      </CardContent>
      <ReplyDialog ticket={replying} onClose={() => setReplying(null)} onSent={load} />
    </Card>
  );
};

export const AdminSupportRequestsTab = () => <HelpDeskTickets />;
